'use client';

import { useDocumentParser } from '@/layers/presentation/context/DocumentParserContext';
import { Button, Card, Chip, Progress } from '@nextui-org/react';
import { AlertCircle, FileText, Maximize2 } from 'lucide-react';
import { useMemo } from 'react';

export default function MinimizedDocParser() {
  const { stages, isProcessing, maximizeParser, uiState, extractedData, jobStatus, error } =
    useDocumentParser();

  // Calculate the current stage and progress
  const { currentStage, progress } = useMemo(() => {
    // Calculate total number of stages
    const totalStages = Object.keys(stages).length;

    // Count completed stages
    const completedStages = Object.values(stages).filter(
      stage => stage.status === 'COMPLETE',
    ).length;

    // Find the current stage (first one that's in progress)
    const currentStageObj = Object.values(stages).find(stage => stage.status === 'IN_PROGRESS');

    // Calculate progress percentage
    const progressValue = totalStages > 0 ? Math.round((completedStages / totalStages) * 100) : 0;

    return {
      currentStage: currentStageObj?.name || 'Ready',
      progress: progressValue,
    };
  }, [stages]);

  // Only show if the UI state is MINIMIZED and we're either processing or have extracted data or have error
  if (uiState !== 'MINIMIZED' || (!isProcessing && !extractedData && !error)) {
    return null;
  }

  // Get summary data for display
  const isComplete = jobStatus === 'COMPLETE';
  const isFailed = jobStatus === 'FAILED';
  const hasData = extractedData?.patientParameters;
  const cancerType = hasData?.cancerType;
  const cancerSubtype = hasData?.cancerSubtype;

  return (
    <Card className="fixed bottom-4 right-4 shadow-lg z-50 p-4 w-[300px]">
      <div className="flex flex-col gap-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <FileText size={16} className={isFailed ? 'text-danger' : 'text-primary'} />
            {isComplete && (
              <Chip size="sm" color="success" variant="flat">
                Ready
              </Chip>
            )}
            {isProcessing && (
              <Chip size="sm" color="primary" variant="flat">
                Processing
              </Chip>
            )}
            {isFailed && (
              <Chip size="sm" color="danger" variant="flat">
                Failed
              </Chip>
            )}
          </div>
          <Button isIconOnly size="sm" variant="light" onPress={maximizeParser}>
            <Maximize2 size={14} />
          </Button>
        </div>

        {isProcessing ? (
          <div className="flex flex-col gap-2">
            <div className="flex justify-between items-center">
              <span className="text-xs text-default-600">{currentStage}</span>
              <span className="text-xs font-medium">{progress}%</span>
            </div>
            <Progress
              size="sm"
              value={progress}
              color="primary"
              aria-label={`${currentStage} - ${progress}%`}
              className="max-w-full"
              isIndeterminate={isProcessing && progress === 0}
            />
          </div>
        ) : isFailed ? (
          <div className="flex items-center gap-2 text-danger text-xs">
            <AlertCircle size={14} />
            <span className="truncate">{error || 'Processing failed'}</span>
          </div>
        ) : hasData ? (
          <div className="flex flex-col gap-1 text-xs">
            {cancerType && (
              <div className="flex justify-between">
                <span className="text-default-500">Cancer type:</span>
                <span className="font-medium truncate max-w-[150px]">{cancerType}</span>
              </div>
            )}
            {cancerSubtype && (
              <div className="flex justify-between">
                <span className="text-default-500">Subtype:</span>
                <span className="font-medium truncate max-w-[150px]">{cancerSubtype}</span>
              </div>
            )}
          </div>
        ) : null}

        <Button
          size="sm"
          color={isFailed ? 'danger' : 'primary'}
          onPress={maximizeParser}
          fullWidth
        >
          {isFailed ? 'View Error' : 'View Details'}
        </Button>
      </div>
    </Card>
  );
}
